import React, { useState } from 'react';

export type IconSize = 'tiny' | 'small' | 'medium' | 'large';

export const sizeMap: Record<IconSize, { suffix: string; dimension: number }> =
  {
    tiny: { suffix: '', dimension: 30 },
    small: { suffix: '', dimension: 80 },
    medium: { suffix: '@2x', dimension: 160 },
    large: { suffix: '@3x', dimension: 240 },
  };

export interface BaseIconProps {
  code: string;
  size?: IconSize;
  className?: string;
  directory: string;
  responsive?: boolean;
  // Optional explicit dimensions that override the size prop if provided
  width?: number;
  height?: number;
}

export function normalizeIconCode(code: string): string {
  const normalized = code
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  const aliases: Record<string, string> = {
    war_hero: 'warhero',
  };

  return aliases[normalized] || normalized;
}

/**
 * BaseIcon component for displaying icons with responsive options
 *
 * Uses the same responsive image handling approach as AssetIcon.
 * By default, now uses responsive mode with the highest quality image.
 */
export function BaseIcon({
  code,
  size = 'small',
  className = '',
  directory,
  responsive = true,
  width,
  height,
}: BaseIconProps) {
  // Base path for all image sizes
  const normalizedCode = normalizeIconCode(code);
  const basePath = `/images/${directory}/${normalizedCode}`;

  // Get dimension from size prop
  const { dimension } = sizeMap[size];

  // For responsive mode, always start with the highest resolution (@3x) image for best quality
  // For fixed size mode, use the appropriate size based on the size prop
  const highestResSuffix = '@3x';
  const initialSrc = responsive
    ? `${basePath}${highestResSuffix}.png`
    : `${basePath}${sizeMap[size].suffix}.png`;

  // For initial src and fallback handling
  const [imgSrc, setImgSrc] = useState(initialSrc);

  // Handle image load error
  const handleError = (_error: React.SyntheticEvent) => {
    const { suffix } = sizeMap[size];
    setImgSrc(`/images/${directory}/default${suffix}.png`);
  };

  // Determine dimensions for responsive mode
  const finalWidth = width || dimension;
  const finalHeight = height || dimension;

  if (responsive) {
    // The responsive mode includes built-in constraints based on the size prop
    // or explicit width/height if provided
    return (
      <div
        className={`relative ${className}`}
        style={{
          width: finalWidth,
          height: finalHeight,
          maxWidth: '100%', // Ensure it doesn't overflow its container
        }}
      >
        <img
          src={imgSrc}
          alt={code.toLowerCase()}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={handleError}
        />
      </div>
    );
  }

  // For non-responsive mode, let Next.js handle responsive loading with our constraints
  return (
    <img
      src={imgSrc}
      alt={code.toLowerCase()}
      width={dimension}
      height={dimension}
      onError={handleError}
    />
  );
}
